import moment from "moment"
import React, { useEffect, useState } from "react"
import { useHistory, useLocation } from "react-router-dom"
import { Card, Col, Container, Row } from "reactstrap"

import {
    GetInventoryWarning,
    GetProductionLineDetails,
    GetProductionLinesListing,
    GetTimelineListing,
} from "api/api.service"

import ProductionScheduleCalendar from "./ProductionScheduleCalendar"
import { useLocalPaginatedRequest } from "hooks/useLocalPaginatedRequest"
import { ProductionScheduleProvider } from "./ProductionScheduleContext"
import { dateTimeFormat, parseDate } from "utils/dateFormatUtils"
import ProductionScheduleHeader from "./ProductionScheduleHeader"
import useAsyncSelect from "hooks/useAsyncSelect"
import { isEmpty } from "utils/commonUtils"
import { ProductionLineStatus } from "constants"
import { BackButtonTableTop } from "components/BackButtonTableTop"
import { TableTopHeading } from "components/TableTopHeading"

import scheduleIcon from "assets/images/icons/schedule-icon.png"

const DATA_KEY = "data"
const TOTAL_KEY = "total"
const UNLIMITED_PRODUCTION_LINE_LIMIT = -1

const initialRangeValue = [
    {
        startDate: undefined,
        endDate: undefined,
        key: "selection",
    },
]

const ProductionSchedule = () => {
    let controller = new AbortController()
    let signal = controller.signal

    const location = useLocation()
    const history = useHistory()
    const { id: timeLineId, productionLineId: productionLineId } =
        (location && location.state) || {}

    const [isLoading, setIsLoading] = useState(false)
    const [productionDetailsData, setProductionDetailsData] = useState({})
    const [productionFilter, setProductionFilter] = useState(null)

    const [range, setRange] = useState(initialRangeValue)
    const [tempRange, setTempRange] = useState(initialRangeValue)
    const [showWarningModal, setShowWarningModal] = useState(false)

    const params = {
        limit: -1,
        productionLineId: productionFilter?.id ?? productionLineId,
        ...getDateRangeParams(range[0]),
    }

    function getDateRangeParams(dateRange) {
        if (!dateRange) return {}
        const params = {}
        if (dateRange.startDate) {
            params.dateFrom = moment
                .utc(dateRange.startDate)
                .startOf("day")
                .format(dateTimeFormat.apiDateTimeFormat)
        }
        if (dateRange.endDate) {
            params.dateTo = moment
                .utc(dateRange.endDate)
                .endOf("day")
                .format(dateTimeFormat.apiDateTimeFormat)
        }
        return params
    }

    const { data: timeLineData, isFetching } = useLocalPaginatedRequest({
        requestFn: getTimeLineListing,
        params,
        deps: [
            productionFilter?.id,
            range,
            productionLineId,
            productionDetailsData?.startDate,
        ],
        dataKey: DATA_KEY,
        totalKey: TOTAL_KEY,
    })

    async function getTimeLineListing() {
        if (!productionLineId && (!productionFilter?.id || !range?.[0]?.key)) {
            return Promise.resolve([])
        }

        const res = await GetTimelineListing(params)
        const timelineData = res?.data

        if (!isEmpty(timelineData)) {
            const dates = timelineData.map(item => moment.utc(item.date))
            // const minDate = moment.min(dates)
            const minDate = new Date(productionDetailsData?.startDate)
            const maxDate = moment.max(dates)

            if (productionDetailsData?.startDate) {
                setTempRange([
                    {
                        startDate: minDate,
                        endDate: maxDate.toDate(),
                        key: "selection",
                        type: "auto",
                    },
                ])
            }
        } else {
            if (!!productionDetailsData?.stages?.length) {
                const minDate = parseDate(productionDetailsData?.startDate)

                const maxDate = productionDetailsData?.startDate
                    ? parseDate(productionDetailsData?.endDate)
                    : moment(productionDetailsData?.startDate)
                          .add(6, "days")
                          .toDate()
                setTempRange([
                    {
                        startDate: minDate,
                        endDate: maxDate,
                        key: "selection",
                        type: "auto",
                    },
                ])
            } else {
                setTempRange(initialRangeValue)
            }
        }

        return res
    }

    useEffect(() => {
        if (productionFilter?.id || productionLineId) {
            fetchProductionLineDetails(productionFilter?.id ?? productionLineId)
        }
    }, [productionFilter?.id, productionLineId])

    const fetchProductionLineDetails = async id => {
        setIsLoading(true)
        GetProductionLineDetails(id)
            .then(detailsRes => {
                if (detailsRes) {
                    if (!productionFilter) {
                        const filterValue = {
                            id: detailsRes?.id,
                            label: detailsRes?.name,
                            value: detailsRes?.id,
                        }
                        setProductionFilter(filterValue)
                    }
                    setProductionDetailsData(detailsRes)
                }
            })
            .catch(err => {
                console.error(err)
            })
            .finally(() => {
                setTimeout(() => setIsLoading(false), 500)
            })
    }

    const {
        selectEntity: productionListing,
        handleSearchOnOptions,
        handleSelectAOption,
    } = useAsyncSelect({
        apiFunc: GetProductionLinesListing,
        dataKey: DATA_KEY,
        totalKey: TOTAL_KEY,
        labelKey: "name",
        valueKey: "id",
        deps: [],
        params: {
            status: ProductionLineStatus.Active,
            limit: UNLIMITED_PRODUCTION_LINE_LIMIT,
        },
        attachSignal: true,
    })

    useEffect(() => {
        if (productionFilter) {
            handleSelectAOption(productionFilter, productionFilter.id)
        }
    }, [productionFilter])

    const handleSetProduction = option => {
        if (typeof option !== "object" || !option) return
        if (option.value === productionFilter?.value) return

        setProductionFilter(option)
        setShowWarningModal(false)
        setRange(initialRangeValue)
        setTempRange(initialRangeValue)
        handleSelectAOption(option, option.id)
    }

    const handleSearch = (value = "") => {
        handleSearchOnOptions(value)
    }

    const handleDateChange = ({ selection }) => {
        const { startDate, endDate } = selection

        let safeEndDate = endDate

        if (!(endDate instanceof Date) || isNaN(endDate)) {
            safeEndDate = startDate
        }

        const adjustedSelection = {
            ...selection,
            endDate: safeEndDate,
        }

        setRange([adjustedSelection])
        setTempRange(initialRangeValue)
    }

    const navigateToDetails = () => {
        // history.push(
        //    getRoute(APP_ROUTES.PRODUCTION_LINE_MANAGEMENT_DETAILS, {
        //       productionLineId: productionFilter?.id ?? productionLineId,
        //    })
        // )
    }

    return (
        <div className="gt-business-accounts px-3 px-md-5">
            <Container fluid>
                <Row>
                    <Col className="col-12 ">
                        <div className="d-flex align-items-center mb-4">
                            <BackButtonTableTop />
                        </div>

                        <TableTopHeading
                            title={"Production Schedule"}
                            iconImage={scheduleIcon}
                            iconClass="scrap-icon"
                        />

                        <ProductionScheduleProvider
                            value={{
                                timeLineId,
                                timeLineData,
                                overrides: productionDetailsData?.overrides,
                                dailyConfigurations:
                                    productionDetailsData?.dailyConfigurations,
                                stages: productionDetailsData?.stages,
                            }}
                        >
                            <ProductionScheduleHeader
                                isLoading={isLoading}
                                range={range}
                                tempRange={tempRange}
                                productionFilter={productionFilter}
                                productionListing={productionListing}
                                onDateChange={handleDateChange}
                                onSetProduction={handleSetProduction}
                                onSearch={handleSearch}
                                onShowNextInventory={() =>
                                    setShowWarningModal(prev => !prev)
                                }
                                isDatePickerDisabled={
                                    !productionFilter?.id && !productionLineId
                                }
                                productionDetailsData={productionDetailsData}
                                navigateToDetails={navigateToDetails}
                            />
                            <Card className="mb-5">
                                <ProductionScheduleCalendar
                                    isLoading={isLoading || isFetching}
                                    timeLineData={timeLineData}
                                    dateRange={range}
                                    productionDetailsData={
                                        productionDetailsData
                                    }
                                    showWarningModal={showWarningModal}
                                    setShowWarningModal={setShowWarningModal}
                                    productionLineId={
                                        productionFilter?.id ?? productionLineId
                                    }
                                />
                            </Card>
                        </ProductionScheduleProvider>
                    </Col>
                </Row>
            </Container>
        </div>
    )
}

export default ProductionSchedule
